/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { concat, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import {
    map,
    switchMap,
    filter,
    catchError,
    withLatestFrom,
    pluck,
} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import moment from 'moment-timezone';

import { SecurityHelper, STATUS } from 'sarsaparilla';

import { convertMilitaryTime } from '../utils';
import { includeFirstComeFirstServed as includeFirstComeFirstServedUtil } from '../utils/generalTicketUtils';
import { getTourTimesUrl } from '../api/endpoints';

import {
    FETCH_TOUR_TIMES,
    fetchTourTimesError,
    fetchTourTimesSuccess,
    setFetchTourTimesStatus,
} from '../actions/tourTimesActions';

function isInstanceForAccountType({ accountType, instance }) {
    const inventoryCount = instance.inventory_count;

    if (accountType === 'FIT') {
        return (
            inventoryCount.ANY ||
            inventoryCount.ANY_SECONDARY ||
            inventoryCount.FIT ||
            inventoryCount.FIT_SECONDARY ||
            inventoryCount.WALKUP
        );
    }

    if (accountType === 'COMM') {
        return inventoryCount.COMM || inventoryCount.COMM_SECONDARY;
    }
    return false;
}

// Helper to get the api response into the correct shape
export const transformApiResponse = ({
    response,
    timezone,
    accountType,
    includeFirstComeFirstServed,
}) => {
    const tourTimes = {};

    if (Array.isArray(response)) {
        const nowMoment = moment().tz(timezone);

        response.forEach((item) => {
            // If this tour instance is not relevant for the current accountType, we skip it
            if (!isInstanceForAccountType({ instance: item, accountType })) {
                return;
            }

            if (!tourTimes[item.tour_id]) {
                tourTimes[item.tour_id] = [];
            }

            let inventoryCount = 0;
            let walkupCount = 0;
            let unreleasedCount = 0;
            let hasSecondary = false;
            const primaryOpenMoment = moment(item.booking_windows.PRIMARY.open_timestamp);
            const primaryCloseMoment = moment(
                item.booking_windows.PRIMARY.close_timestamp
            );
            const secondaryOpenMoment = item.booking_windows?.SECONDARY?.open_timestamp
                ? moment(item.booking_windows.SECONDARY.open_timestamp)
                : primaryOpenMoment;
            const secondaryCloseMoment = item.booking_windows?.SECONDARY?.close_timestamp
                ? moment(item.booking_windows.SECONDARY.close_timestamp)
                : primaryCloseMoment;
            const tourStartMoment = moment(item?.tour_start_timestamp);

            let firstReleasedTime = null;
            let secondReleasedTime = null;
            let moreAvailableReleaseTime = null;
            let secondaryMoreAvailableReleaseTime = null;
            let primaryUnreleasedCount = 0;
            let secondaryUnreleasedCount = 0;
            let reservedCount = 0;
            let walkupReservedCount = 0;

            Object.keys(item?.reservation_count).forEach((key) => {
                // Check if the reservation type is allowed for the account
                if (
                    isInstanceForAccountType({
                        accountType,
                        instance: {
                            inventory_count: {
                                [key]: item.reservation_count[key],
                            },
                        },
                    })
                ) {
                    reservedCount += item.reservation_count[key];

                    if (includeFirstComeFirstServed && key === 'WALKUP') {
                        walkupReservedCount += item.reservation_count[key];
                    }
                }
            });

            Object.keys(item?.inventory_count).forEach((key) => {
                // Check if the inventory type is allowed for the account
                if (
                    isInstanceForAccountType({
                        accountType,
                        instance: {
                            inventory_count: {
                                [key]: 1,
                            },
                        },
                    })
                ) {
                    // If secondary
                    if (key.includes('SECONDARY')) {
                        // The date the secondary release becomes available is in the booking
                        // window data OR we use the day before as a default
                        if (
                            !nowMoment.isBefore(secondaryOpenMoment) &&
                            !nowMoment.isAfter(secondaryCloseMoment)
                        ) {
                            inventoryCount += item.inventory_count[key];
                            let newFirstReleasedTime = secondaryOpenMoment;

                            if (
                                !firstReleasedTime ||
                                newFirstReleasedTime?.isBefore(firstReleasedTime)
                            ) {
                                firstReleasedTime = newFirstReleasedTime?.clone();
                                newFirstReleasedTime = null;
                            }

                            if (
                                firstReleasedTime &&
                                newFirstReleasedTime &&
                                !newFirstReleasedTime?.isSame(firstReleasedTime) &&
                                (!secondReleasedTime ||
                                    newFirstReleasedTime?.isBefore(secondReleasedTime))
                            ) {
                                secondReleasedTime = newFirstReleasedTime;
                            }
                        } else if (!nowMoment.isAfter(secondaryCloseMoment)) {
                            const inventoryForThisType = item.inventory_count[key];
                            const availableCount = inventoryCount - reservedCount;

                            // we want to show unreleasedCount ONLY for PRIMARY release first, then SECONDARY once
                            // PRIMARY has been depleted, so only populate this if unreleasedCount has already
                            // been populated (from PRIMARY)
                            if (!unreleasedCount) unreleasedCount = inventoryForThisType;
                            secondaryUnreleasedCount = inventoryForThisType;
                            if (inventoryForThisType > 0 && availableCount <= 0) {
                                secondaryMoreAvailableReleaseTime =
                                    secondaryOpenMoment.format();
                            }
                        } else if (
                            includeFirstComeFirstServed &&
                            nowMoment.isAfter(secondaryCloseMoment) &&
                            nowMoment?.isSameOrBefore(tourStartMoment)
                        ) {
                            const itemCount =
                                item.inventory_count[key] - item.reservation_count[key] ||
                                0;
                            walkupCount += itemCount;
                        }

                        // secondary inventory defaults to 0 so we need an explicit check
                        if (item.inventory_count[key] > 0) {
                            hasSecondary = true;
                        }
                    }
                    // walkup
                    else if (key === 'WALKUP') {
                        if (
                            includeFirstComeFirstServed &&
                            nowMoment?.isSameOrBefore(tourStartMoment)
                        ) {
                            walkupCount += item.inventory_count[key];
                        }
                    }
                    // Everything else that's not after close time
                    else if (
                        !nowMoment.isBefore(primaryOpenMoment) &&
                        !nowMoment.isAfter(primaryCloseMoment)
                    ) {
                        inventoryCount += item.inventory_count[key];

                        let newFirstReleasedTime = null;

                        if (nowMoment.isAfter(primaryOpenMoment)) {
                            newFirstReleasedTime = primaryOpenMoment;
                        }

                        if (
                            !firstReleasedTime ||
                            newFirstReleasedTime?.isBefore(firstReleasedTime)
                        ) {
                            firstReleasedTime = newFirstReleasedTime?.clone();
                            newFirstReleasedTime = null;
                        }

                        if (
                            firstReleasedTime &&
                            newFirstReleasedTime &&
                            !newFirstReleasedTime?.isSame(firstReleasedTime) &&
                            (!secondReleasedTime ||
                                newFirstReleasedTime?.isBefore(secondReleasedTime))
                        ) {
                            secondReleasedTime = newFirstReleasedTime;
                        }
                    }
                    // Primary and before release
                    else if (!nowMoment.isAfter(primaryCloseMoment)) {
                        // we want to show unreleasedCount ONLY for PRIMARY release first, then SECONDARY once
                        // PRIMARY has been depleted, so overwrite unreleasedCount if PRIMARY unreleased is
                        // available
                        // betty note: this only works because the last item in the map is a primary quota.
                        // if the last item is a secondary quota, this does not work at all
                        if (item.inventory_count[key]) {
                            unreleasedCount = item.inventory_count[key];
                            primaryUnreleasedCount = item.inventory_count[key];
                        }

                        if (item.inventory_count[key] > 0) {
                            if (nowMoment.isBefore(primaryOpenMoment)) {
                                moreAvailableReleaseTime = primaryOpenMoment.format();
                            }
                        }
                    } else if (
                        includeFirstComeFirstServed &&
                        nowMoment.isAfter(primaryCloseMoment) &&
                        nowMoment?.isSameOrBefore(tourStartMoment)
                    ) {
                        const itemCount =
                            item.inventory_count[key] - item.reservation_count[key] || 0;
                        walkupCount += itemCount;
                    }
                }
            });

            // if the primary release time was never set (aka we're between primary open and close times)
            // OR if someone published a schedule with JUST secondary quota AND filled out the primary set
            // release time. In either of these cases, show the secondary release time.
            if (
                moreAvailableReleaseTime === null ||
                (secondaryUnreleasedCount > 0 && primaryUnreleasedCount <= 0)
            ) {
                moreAvailableReleaseTime = secondaryMoreAvailableReleaseTime;
                secondaryMoreAvailableReleaseTime = null;
            }

            let availableCount = inventoryCount > 0 ? inventoryCount - reservedCount : 0;
            // don't display negative availability externally if numbers are in that state
            availableCount = availableCount < 0 ? 0 : availableCount;

            // walk up counters
            let availableWalkupCount =
                walkupCount > 0 ? walkupCount - walkupReservedCount : 0;
            // don't display negative availability externally if numbers are in that state
            availableWalkupCount = availableWalkupCount < 0 ? 0 : availableWalkupCount;

            const startTime = convertMilitaryTime(item.tour_time);
            const endTime = moment.parseZone(item.tour_end_timestamp).format('h:mm A');

            tourTimes[item.tour_id].push({
                primaryEmpty:
                    !item.inventory_count?.ANY && !item.inventory_count?.[accountType],
                timeStamp: item.tour_start_timestamp,
                availableCount,
                availableWalkupCount,
                walkupCount,
                unreleasedCount,
                firstReleasedTime: firstReleasedTime?.format() ?? '',
                secondReleasedTime: secondReleasedTime?.format() ?? '',
                moreAvailableReleaseTime,
                secondaryMoreAvailableReleaseTime,
                nowMoment,
                timezone,
                primaryOpenMoment,
                secondaryOpenMoment,
                rollingReleaseDate: item.rolling_release_date,
                isAvailable: availableCount > 0,
                isWalkupAvailable: availableWalkupCount > 0,
                time: item.tour_time,
                displayTime: startTime,
                displayDuration: `${startTime} - ${endTime}`,
                bookingCloseTime:
                    hasSecondary && item.booking_windows?.SECONDARY
                        ? item.booking_windows.SECONDARY.close_timestamp
                        : item.booking_windows.PRIMARY.close_timestamp,
                hasSecondary,
                hasScheduledInventory: inventoryCount > 0,
            });
        });
    }

    return tourTimes;
};

/**
 * Fetch all the tour times for a given day and facilityId, or tourId or reservationId
 */
export const fetchTourTimes = (actions$, state$) =>
    actions$.pipe(
        ofType(FETCH_TOUR_TIMES),
        filter(({ payload }) => !!payload),
        withLatestFrom(state$.pipe(pluck('timedEntry'))),
        switchMap(([{ payload }, timedEntry]) => {
            const { date } = payload;
            const timezone = timedEntry.facility?.facility_timezone;
            const facilityId = timedEntry.facility?.facility_id;
            const accountType = timedEntry.accountType.accountType;
            const includeFirstComeFirstServed =
                includeFirstComeFirstServedUtil(facilityId);

            return concat(
                of(setFetchTourTimesStatus(STATUS.PENDING)),
                ajax({
                    url: getTourTimesUrl(payload),
                    method: 'GET',
                    headers: {
                        Authorization: SecurityHelper.getAuthHeader(),
                    },
                })
                    .pipe(pluck('response'))
                    .pipe(
                        map((response) => {
                            const data = transformApiResponse({
                                response,
                                timezone,
                                accountType,
                                includeFirstComeFirstServed,
                            });

                            return fetchTourTimesSuccess({ data, date });
                        }),
                        catchError((error) => of(fetchTourTimesError(error)))
                    )
            );
        })
    );
